import React, { Fragment, useRef, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { twClassNames } from "../../../helpers/twClassNames";
import { Chip } from "../../Chip";

type Tier = {
    credits: number;
    monthlyPrice: number;
    annualPrice: number;
    monthlyDiscountPercent: number;
    annualDiscountPercent: number;
    isDefault?: boolean;
};

type CreditTierSelectorProps = {
    tiers: Tier[];
    selectedTier: Tier;
    onSelect: (tier: Tier) => void;
    isAnnual: boolean;
    baseTier: Tier;
    compact?: boolean;
};

function getPrice(tier: Tier, isAnnual: boolean): number {
    return isAnnual ? tier.annualPrice : tier.monthlyPrice;
}

function getDiscount(tier: Tier, isAnnual: boolean): number {
    return isAnnual ? tier.annualDiscountPercent : tier.monthlyDiscountPercent;
}

function getIncrementalCost(tier: Tier, baseTier: Tier, isAnnual: boolean): number | null {
    const tierPrice = getPrice(tier, isAnnual);
    const basePrice = getPrice(baseTier, isAnnual);
    const diff = tierPrice - basePrice;
    return diff > 0 ? diff : null;
}

function getIntervalCredits(tier: Tier, isAnnual: boolean): number {
    return isAnnual ? tier.credits * 12 : tier.credits;
}

function getIntervalDisplayText(isAnnual: boolean): string {
    return isAnnual ? "year" : "month";
}

export function CreditTierSelector({
    tiers,
    selectedTier,
    onSelect,
    isAnnual,
    baseTier,
    compact = false
}: CreditTierSelectorProps) {
    const discount = getDiscount(selectedTier, isAnnual);
    const [pendingTier, setPendingTier] = useState<Tier | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const handleSelect = (tier: Tier) => {
        setPendingTier(tier);
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            onSelect(tier);
            setPendingTier(null);
        }, 150);
    };

    const intervalDisplayText = getIntervalDisplayText(isAnnual);

    return (
        <Listbox value={pendingTier ?? selectedTier} onChange={handleSelect}>
            {({ open }) => (
                <div className="relative">
                    <Listbox.Button className="flex w-full items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left text-sm hover:border-gray-300">
                        <div className="flex w-full items-center justify-between gap-2 self-start">
                            <span className="font-medium text-gray-900">
                                {getIntervalCredits(selectedTier, isAnnual)} credits{" "}
                                <span className="font-normal text-gray-500">
                                    / {intervalDisplayText}
                                </span>
                            </span>
                            <Chip
                                color="yellow"
                                className={discount > 0 && !open ? "" : "invisible"}
                            >
                                Save {discount || 0}% per credit
                            </Chip>
                        </div>
                        <i className="far fa-chevron-down text-xs text-gray-400" />
                    </Listbox.Button>

                    <Transition
                        as={Fragment}
                        enter="transition duration-100 ease-out"
                        enterFrom="transform scale-95 opacity-0"
                        enterTo="transform scale-100 opacity-100"
                        leave="transition duration-75 ease-out"
                        leaveFrom="transform scale-100 opacity-100"
                        leaveTo="transform scale-95 opacity-0"
                    >
                        <Listbox.Options className="absolute left-0 top-full z-50 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                            {tiers.map((tier) => {
                                const tierDiscount = getDiscount(tier, isAnnual);
                                const incrementalCost = getIncrementalCost(
                                    tier,
                                    baseTier,
                                    isAnnual
                                );
                                const intervalCredits = getIntervalCredits(tier, isAnnual);

                                return (
                                    <Listbox.Option
                                        key={intervalCredits}
                                        value={tier}
                                        as={Fragment}
                                    >
                                        {({ active, selected }) => (
                                            <li
                                                className={twClassNames(
                                                    "flex cursor-pointer px-3 py-2.5 text-sm hover:bg-gray-100",
                                                    compact
                                                        ? "flex-col gap-1"
                                                        : "items-center justify-between",
                                                    selected &&
                                                        "rounded-lg border border-yellow-700"
                                                )}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span
                                                        className={twClassNames(
                                                            "font-medium",
                                                            !compact && "w-[132px]",
                                                            selected
                                                                ? "text-yellow-900"
                                                                : "text-gray-900"
                                                        )}
                                                    >
                                                        {intervalCredits} credits{" "}
                                                        <span
                                                            className={twClassNames(
                                                                "font-normal",
                                                                selected
                                                                    ? "text-yellow-900"
                                                                    : "text-gray-500"
                                                            )}
                                                        >
                                                            / {intervalDisplayText}
                                                        </span>
                                                    </span>
                                                    {!compact && incrementalCost != null && (
                                                        <Chip color="gray">
                                                            +${incrementalCost}/mo
                                                        </Chip>
                                                    )}
                                                </div>
                                                <div
                                                    className={twClassNames(
                                                        "flex items-center gap-1",
                                                        compact && "flex-wrap"
                                                    )}
                                                >
                                                    {compact && incrementalCost != null && (
                                                        <Chip color="gray">
                                                            +${incrementalCost}
                                                            {isAnnual ? "/mo" : ""}
                                                        </Chip>
                                                    )}
                                                    {tierDiscount > 0 && (
                                                        <Chip color="yellow" className="w-32">
                                                            Save {tierDiscount}% per credit
                                                        </Chip>
                                                    )}
                                                </div>
                                            </li>
                                        )}
                                    </Listbox.Option>
                                );
                            })}
                        </Listbox.Options>
                    </Transition>
                </div>
            )}
        </Listbox>
    );
}
